import React, { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { Button, Card, Menu, Switch, Text } from 'react-native-paper';
import { colors, spacing, typography } from '../../../constants/theme';
import { AdminDashboardTab, ReviewTemplate } from '../AdminDashboard.types';

interface AdminDashboardHeaderProps {
  activeTab: AdminDashboardTab;
  categoriesCount: number;
  includeDeactivated: boolean;
  itemsCount: number;
  onBack?: () => void;
  onChangeIncludeDeactivated: (includeDeactivated: boolean) => void;
  onChangeTab: (tab: AdminDashboardTab) => void;
  onSelectTemplate: (templateId: number) => void;
  selectedTemplateId: number | null;
  sharingCount: number;
  templates: ReviewTemplate[];
  templatesCount: number;
}

export const AdminDashboardHeader: React.FC<AdminDashboardHeaderProps> = ({
  activeTab,
  categoriesCount,
  includeDeactivated,
  itemsCount,
  onBack,
  onChangeIncludeDeactivated,
  onChangeTab,
  onSelectTemplate,
  selectedTemplateId,
  sharingCount,
  templates,
  templatesCount,
}) => {
  const [templateMenuVisible, setTemplateMenuVisible] = useState(false);
  const selectedTemplate = templates.find(template => template.id === selectedTemplateId);

  return (
    <Card style={styles.headerCard}>
      <Card.Content>
        {onBack && (
          <View style={styles.backButtonContainer}>
            <Button
              testID="admin-back-button"
              mode="text"
              onPress={onBack}
              style={styles.backButton}
              icon="arrow-left"
            >
              Back to Admin Menu
            </Button>
          </View>
        )}
        <Text style={styles.title}>Review Templates</Text>
        <Text style={styles.subtitle}>Manage Review Items and Categories</Text>

        <View style={styles.controlsContainer}>
          <View style={styles.templateSelectorContainer}>
            <Text style={styles.controlLabel}>Review Type:</Text>
            <Menu
              visible={templateMenuVisible}
              onDismiss={() => setTemplateMenuVisible(false)}
              anchor={
                <Button
                  testID="admin-template-selector"
                  accessibilityLabel={selectedTemplate
                    ? `${selectedTemplate.name} (${selectedTemplate.type})`
                    : 'Loading templates'}
                  mode="outlined"
                  onPress={() => setTemplateMenuVisible(true)}
                  style={styles.templateSelector}
                >
                  {selectedTemplateId
                    ? (selectedTemplate?.name || `Template ${selectedTemplateId}`)
                    : 'Loading templates...'}
                </Button>
              }
            >
              {templates.map(template => (
                <Menu.Item
                  key={template.id}
                  testID={`admin-template-option-${template.id}`}
                  title={`${template.name} (${template.type})`}
                  onPress={() => {
                    onSelectTemplate(template.id);
                    setTemplateMenuVisible(false);
                  }}
                />
              ))}
            </Menu>
          </View>

          <View style={styles.toggleContainer}>
            <Text style={styles.controlLabel}>Include Deactivated Items</Text>
            <Switch
              value={includeDeactivated}
              onValueChange={onChangeIncludeDeactivated}
            />
          </View>
        </View>

        <View style={styles.tabContainer}>
          <Button
            testID="admin-templates-items-tab"
            mode={activeTab === 'items' ? 'contained' : 'outlined'}
            onPress={() => onChangeTab('items')}
            style={styles.tabButton}
          >
            Items ({itemsCount})
          </Button>
          <Button
            testID="admin-templates-categories-tab"
            mode={activeTab === 'categories' ? 'contained' : 'outlined'}
            onPress={() => onChangeTab('categories')}
            style={styles.tabButton}
          >
            Categories ({categoriesCount})
          </Button>
          <Button
            testID="admin-templates-templates-tab"
            mode={activeTab === 'templates' ? 'contained' : 'outlined'}
            onPress={() => onChangeTab('templates')}
            style={styles.tabButton}
          >
            Templates ({templatesCount})
          </Button>
          <Button
            testID="admin-templates-sharing-tab"
            mode={activeTab === 'sharing' ? 'contained' : 'outlined'}
            onPress={() => onChangeTab('sharing')}
            style={styles.tabButton}
          >
            Sharing ({sharingCount})
          </Button>
          <Button
            testID="admin-templates-reports-tab"
            mode={activeTab === 'reports' ? 'contained' : 'outlined'}
            onPress={() => onChangeTab('reports')}
            style={styles.tabButton}
          >
            Reports
          </Button>
        </View>
      </Card.Content>
    </Card>
  );
};

const styles = StyleSheet.create({
  headerCard: {
    marginBottom: spacing.lg,
  },
  backButtonContainer: {
    marginBottom: spacing.md,
  },
  backButton: {
    alignSelf: 'flex-start',
    marginLeft: -spacing.sm,
  },
  title: {
    ...typography.h1,
    marginBottom: spacing.sm,
  },
  subtitle: {
    ...typography.body,
    color: colors.textSecondary,
    marginBottom: spacing.md,
  },
  controlsContainer: {
    gap: spacing.md,
    marginBottom: spacing.md,
  },
  templateSelectorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  controlLabel: {
    ...typography.body,
    minWidth: 120,
  },
  templateSelector: {
    flex: 1,
    minWidth: 200,
  },
  toggleContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  tabContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: spacing.md,
    gap: spacing.sm,
  },
  tabButton: {
    flex: 1,
    minWidth: 120,
  },
});

export default AdminDashboardHeader;
